import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import {
    listBaskets, createBasket, fetchCompetitorData,
    getCompetitorData, deleteBasket,
    parseCsvFile, parseAsinListCsv, toggleBasketAutoFetch, patchBasket, fetchCompetitorDataFromList
} from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ───────────────────────────────────────────── */
/* Tracking Overview — port of tracking2 home.py */
/* ───────────────────────────────────────────── */

const ProductCard = ({ row }) => {
    const [showDetails, setShowDetails] = useState(false);

    // Helper to format currency
    const fmtPrice = (p) => p != null ? `$${Number(p).toFixed(2)}` : '—';
    const fmtNum = (n) => n != null ? Number(n).toLocaleString() : '—';

    return (
        <div className="card h-100 shadow-sm">
            {row.product_photo ? (
                <img src={row.product_photo} className="card-img-top p-2" alt={row.asin} style={{ height: '180px', objectFit: 'contain' }} />
            ) : (
                <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: '180px' }}>
                    <i className="bi bi-box-seam fs-1 text-muted"></i>
                </div>
            )}
            <div className="card-body p-3 d-flex flex-column">
                <div className="mb-2">
                    <small className="text-muted fw-bold">{row.brand}</small>
                    <small className="text-muted mx-1">—</small>
                    <small className="text-primary font-monospace">{row.asin}</small>
                </div>

                <h6 className="card-title text-truncate-2" title={row.product_title} style={{ minHeight: '40px', fontSize: '0.9rem', lineHeight: '1.3' }}>
                    {row.product_title || 'No title available'}
                </h6>

                <div className="mt-auto pt-2">
                    <a href={row.product_url} target="_blank" rel="noopener noreferrer" className="d-block text-decoration-none mb-2 small">
                        <i className="bi bi-box-arrow-up-right me-1"></i> Open product page
                    </a>

                    <button
                        className={`btn btn-sm w-100 ${showDetails ? 'btn-secondary' : 'btn-outline-primary'}`}
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        {showDetails ? 'Hide Details' : 'Details'} <i className={`bi bi-chevron-${showDetails ? 'up' : 'down'}`}></i>
                    </button>
                </div>

                {showDetails && (
                    <div className="mt-3 pt-2 border-top small text-start bg-light rounded p-2">
                        <div className="mb-1"><strong>Price:</strong> {fmtPrice(row.product_price)}</div>
                        {row.product_original_price && (
                            <div className="mb-1"><strong>Original:</strong> <span className="text-decoration-line-through text-muted">{fmtPrice(row.product_original_price)}</span></div>
                        )}
                        <div className="mb-1"><strong>Rating:</strong> ⭐ {row.product_star_rating || '-'} ({fmtNum(row.product_num_ratings)})</div>
                        {row.discount && <div className="mb-1"><strong>Discount:</strong> <span className="text-danger">-{Number(row.discount)}%</span></div>}
                        {row.sales_volume && <div className="mb-1"><strong>Sales:</strong> {row.sales_volume}</div>}
                        {row.rank_value && <div className="mb-1"><strong>Rank:</strong> #{fmtNum(row.rank_value)}</div>}
                        {row.sub_category_name && <div className="mb-1 text-muted fst-italic">{row.sub_category_name}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};

function TrackingOverview() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState('week');
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState({ from: '', to: '' });
    const [csvName, setCsvName] = useState(null);
    const [csvFile, setCsvFile] = useState(null);

    // New basket form
    const [showNewBasket, setShowNewBasket] = useState(false);
    const [newName, setNewName] = useState('');
    const [newAsins, setNewAsins] = useState('');
    const [newCountry, setNewCountry] = useState('US');
    const [showEditBasket, setShowEditBasket] = useState(false);
    const [editingBasket, setEditingBasket] = useState(null);
    const [editName, setEditName] = useState('');
    const [editAsins, setEditAsins] = useState('');
    const [editCountry, setEditCountry] = useState('US');

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    // Load baskets
    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try {
            const res = await listBaskets(accountId);
            setBaskets(res.data || []);
        } catch (err) {
            console.error('Error loading baskets:', err);
        }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    // Load data when basket selected (skip for CSV baskets — data already loaded)
    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        setError('');
        try {
            const res = await getCompetitorData(selectedBasket.id, accountId);
            const rows = res.data || [];
            setData(rows);
            const brands = [...new Set(rows.map(r => r.brand).filter(Boolean))].sort();
            setSelectedBrands(brands);
            // Set date range
            const dates = rows.map(r => r.date).filter(Boolean).sort();
            if (dates.length > 0) {
                setDateRange({ from: dates[0], to: dates[dates.length - 1] });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Create basket
    const handleCreateBasket = async (e) => {
        e.preventDefault();
        const asins = newAsins.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
        if (!newName || asins.length === 0) return;
        try {
            await createBasket({ name: newName, asins, country: newCountry, accountId });
            setNewName(''); setNewAsins(''); setShowNewBasket(false);
            loadBaskets();
        } catch (err) {
            setError(err.message);
        }
    };

    // Fetch fresh data
    const handleFetchData = async () => {
        if (!selectedBasket) return;

        setLoading(true);
        setError('');
        try {
            if (selectedBasket.id === 'csv') {
                if (!Array.isArray(selectedBasket.asins) || selectedBasket.asins.length === 0) {
                    setError('CSV list has no ASINs. Please edit or upload a valid CSV.');
                    return;
                }

                const res = await fetchCompetitorDataFromList(
                    selectedBasket.asins,
                    accountId,
                    selectedBasket.country || 'US'
                );
                const rows = res.data || [];
                setData(rows);

                const brands = [...new Set(rows.map(r => r.brand).filter(Boolean))].sort();
                setSelectedBrands(brands);

                const dates = rows.map(r => r.date).filter(Boolean).sort();
                if (dates.length > 0) {
                    setDateRange({ from: dates[0], to: dates[dates.length - 1] });
                }
                return;
            }

            await fetchCompetitorData(selectedBasket.id, accountId);
            await loadData();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Delete basket
    const handleDeleteBasket = async (id) => {
        if (!window.confirm('¿Eliminar este basket y sus datos?')) return;
        try {
            await deleteBasket(id);
            if (selectedBasket?.id === id) { setSelectedBasket(null); setData([]); }
            loadBaskets();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggleAutoFetch = async (basket, e) => {
        e.stopPropagation();
        try {
            const res = await toggleBasketAutoFetch(basket.id);
            setBaskets(prev => prev.map(b => b.id === basket.id ? { ...b, auto_fetch_enabled: res.data.auto_fetch_enabled } : b));
            if (selectedBasket?.id === basket.id) {
                setSelectedBasket(prev => ({ ...prev, auto_fetch_enabled: res.data.auto_fetch_enabled }));
            }
        } catch (err) {
            setError('Failed to toggle auto-fetch: ' + (err.response?.data?.error || err.message));
        }
    };

    const openEditBasket = (basket) => {
        const asins = Array.isArray(basket?.asins) ? basket.asins : [];
        setEditingBasket(basket);
        setEditName(basket?.name || '');
        setEditCountry(basket?.country || 'US');
        setEditAsins(asins.join('\n'));
        setShowEditBasket(true);
    };

    const handleOpenEditFromSelection = () => {
        if (!selectedBasket) return;
        openEditBasket(selectedBasket);
    };

    const handleEditBasket = async (e) => {
        e.preventDefault();
        if (!editingBasket) return;

        const nextAsins = editAsins
            .split(/[\n,]+/)
            .map(s => s.trim().toUpperCase())
            .filter(Boolean);

        const prevAsins = Array.isArray(editingBasket.asins)
            ? editingBasket.asins.map(a => String(a).trim().toUpperCase())
            : [];

        const prevSet = new Set(prevAsins);
        const nextSet = new Set(nextAsins);

        const add_asins = [...nextSet].filter(a => !prevSet.has(a));
        const remove_asins = [...prevSet].filter(a => !nextSet.has(a));

        try {
            if (editingBasket.id === 'csv') {
                setSelectedBasket(prev => ({
                    ...prev,
                    name: editName,
                    country: editCountry,
                    asins: nextAsins,
                }));
                setShowEditBasket(false);
                setEditingBasket(null);
                return;
            }

            const res = await patchBasket(editingBasket.id, {
                name: editName,
                country: editCountry,
                add_asins,
                remove_asins,
            });

            setBaskets(prev => prev.map(b => b.id === editingBasket.id ? { ...b, ...res.data } : b));

            if (selectedBasket?.id === editingBasket.id) {
                setSelectedBasket(prev => ({ ...prev, ...res.data }));
            }

            setShowEditBasket(false);
            setEditingBasket(null);
            await loadBaskets();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    // CSV upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        setError('');
        try {
            const parsed = await parseAsinListCsv(file);
            const asins = parsed.data || [];

            if (asins.length === 0) {
                setError('CSV without ASINs in first column. Expected format: ASIN,optional_label');
                return;
            }

            setData([]);
            setCsvName(file.name);
            setCsvFile(file);

            setSelectedBasket({ id: 'csv', name: file.name, asins, country: 'US' });
            setSelectedBrands([]);
            setDateRange({ from: '', to: '' });
        } catch (err) {
            setError('Error parsing CSV: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClearCsv = () => {
        setCsvName(null);
        setCsvFile(null);
        setSelectedBasket(null);
        setData([]);
        setSelectedBrands([]);
    };

    // ── Derived data ──
    const allBrands = useMemo(() =>
        [...new Set(data.map(r => r.brand).filter(Boolean))].sort(), [data]);

    // Filter by brands and date range
    const filteredData = useMemo(() => {
        let rows = data.filter(r => selectedBrands.includes(r.brand));
        if (dateRange.from) rows = rows.filter(r => r.date >= dateRange.from);
        if (dateRange.to) rows = rows.filter(r => r.date <= dateRange.to);
        return rows;
    }, [data, selectedBrands, dateRange]);

    // ── Highlights (port of highlights_section.py) ──
    const highlights = useMemo(() => {
        if (filteredData.length === 0) return null;

        // Get last period data
        let periodData;
        let label;
        if (period === 'week') {
            const weeks = filteredData.map(r => r.week || r.week_number).filter(Boolean);
            const lastWeek = Math.max(...weeks);
            periodData = filteredData.filter(r => (r.week || r.week_number) === lastWeek);
            label = `week ${lastWeek}`;
        } else {
            const dates = filteredData.map(r => r.date).filter(Boolean).sort();
            const lastDay = dates[dates.length - 1];
            periodData = filteredData.filter(r => r.date === lastDay);
            label = lastDay;
        }

        if (periodData.length === 0) return { label };

        // Discount %
        const withDiscount = periodData.map(r => {
            const origPrice = r.product_original_price;
            const curPrice = r.product_price;
            const discountPct = (origPrice && origPrice > 0)
                ? ((origPrice - curPrice) / origPrice * 100)
                : null;
            return { ...r, discount_pct: discountPct };
        });

        // Extremes
        const validDiscounts = withDiscount.filter(r => r.discount_pct != null);
        const highestDisc = validDiscounts.length ? validDiscounts.reduce((a, b) => Number(a.discount_pct) > Number(b.discount_pct) ? a : b) : null;
        const lowestDisc = validDiscounts.length ? validDiscounts.reduce((a, b) => Number(a.discount_pct) < Number(b.discount_pct) ? a : b) : null;

        const validPrices = periodData.filter(r => r.product_price != null);
        const highestPrice = validPrices.length ? validPrices.reduce((a, b) => Number(a.product_price) > Number(b.product_price) ? a : b) : null;
        const lowestPrice = validPrices.length ? validPrices.reduce((a, b) => Number(a.product_price) < Number(b.product_price) ? a : b) : null;

        // Price change extremes
        const validChanges = periodData.filter(r => r.price_change != null);
        const largestChange = validChanges.length ? validChanges.reduce((a, b) => Number(a.price_change) > Number(b.price_change) ? a : b) : null;
        const lowestChange = validChanges.length ? validChanges.reduce((a, b) => Number(a.price_change) < Number(b.price_change) ? a : b) : null;

        // Averages
        const avgDiscount = validDiscounts.length
            ? (validDiscounts.reduce((s, r) => s + Number(r.discount_pct), 0) / validDiscounts.length)
            : null;
        const avgPrice = validPrices.length
            ? (validPrices.reduce((s, r) => s + Number(r.product_price), 0) / validPrices.length)
            : null;
        const avgChange = validChanges.length
            ? (validChanges.reduce((s, r) => s + Number(r.price_change), 0) / validChanges.length)
            : null;

        return {
            label, highestDisc, lowestDisc,
            highestPrice, lowestPrice,
            largestChange, lowestChange,
            avgDiscount, avgPrice, avgChange,
        };
    }, [filteredData, period]);

    // ── Chart traces ──
    const groupKey = period === 'day' ? 'date' : 'week';
    const buildOverviewTraces = () => {
        const traces = [];
        const grouped = {};
        filteredData.forEach(row => {
            const brand = row.brand || 'Unknown';
            if (!grouped[brand]) grouped[brand] = {};
            const key = row[groupKey] || row.week_number;
            if (!grouped[brand][key]) grouped[brand][key] = [];
            grouped[brand][key].push(row.product_price);
        });

        Object.entries(grouped).forEach(([brand, points]) => {
            const keys = Object.keys(points).sort((a, b) => period === 'week' ? Number(a) - Number(b) : a.localeCompare(b));
            traces.push({
                x: keys,
                y: keys.map(k => {
                    const prices = points[k].filter(p => p != null).map(p => Number(p));
                    return prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : null;
                }),
                mode: 'lines+markers',
                name: brand,
                hovertemplate: `Brand: ${brand}<br>Price: $%{y:.2f}<br>${period === 'day' ? 'Date' : 'Week'}: %{x}<extra></extra>`,
            });
        });
        return traces;
    };

    // Last update
    const latestDate = data.length ? data.reduce((max, r) => r.date > max ? r.date : max, data[0]?.date || '') : null;

    // Toggle brand
    const toggleBrand = (brand) => {
        setSelectedBrands(prev =>
            prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
        );
    };

    // Remove brand (tag close)
    const removeBrand = (brand) => {
        setSelectedBrands(prev => prev.filter(b => b !== brand));
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                {/* ── Header ── */}
                <div className="text-center mb-3">
                    <h2 className="fw-bold" style={{ fontSize: '2rem' }}>Competitor Price Monitoring</h2>
                    <small className="text-muted">
                        Last update: {latestDate || 'N/A'}
                    </small>
                    <div className="text-muted small mt-1">Shared Library: all users can view the same baskets.</div>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* ── Basket selector row ── */}
                <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
                    {selectedBasket && (
                        <span className="badge bg-primary px-3 py-2" style={{ fontSize: '0.9rem' }}>
                            Current source: <strong>{selectedBasket.id === 'csv' ? `CSV - ${selectedBasket.name}` : selectedBasket.name}</strong>
                        </span>
                    )}
                    <div className="dropdown">
                        <button className="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="bi bi-basket2 me-1"></i> Change basket
                        </button>
                        <ul className="dropdown-menu">
                            {baskets.map(b => {
                                const isSelected = selectedBasket?.id === b.id;
                                return (
                                <li key={b.id}>
                                    <button className="dropdown-item d-flex justify-content-between align-items-center"
                                        style={isSelected ? {
                                            backgroundColor: '#eaf2ff',
                                            borderLeft: '3px solid #0d6efd',
                                            color: '#1f2937'
                                        } : undefined}
                                        onClick={() => { setSelectedBasket(b); }}>
                                        <span className="d-flex align-items-center gap-2">
                                            {b.name} ({b.asins?.length || 0} ASINs)
                                            {b.auto_fetch_enabled && (
                                                <span className="badge bg-primary" style={{ fontSize: '0.7rem' }}>Auto</span>
                                            )}
                                        </span>
                                        <span className="d-flex align-items-center gap-2 ms-3">
                                            <i className="bi bi-pencil-square text-primary"
                                                title="Edit basket"
                                                onClick={(e) => { e.stopPropagation(); openEditBasket(b); }}></i>
                                            <i
                                                className={`bi bi-calendar-check${b.auto_fetch_enabled ? '-fill text-primary' : ' text-secondary'}`}
                                                title={b.auto_fetch_enabled ? 'Auto-fetch enabled (click to disable)' : 'Auto-fetch disabled (click to enable)'}
                                                onClick={(e) => handleToggleAutoFetch(b, e)}
                                                style={{ cursor: 'pointer', fontSize: '1rem' }}
                                            ></i>
                                            <i className="bi bi-trash text-danger"
                                                onClick={(e) => { e.stopPropagation(); handleDeleteBasket(b.id); }}></i>
                                        </span>
                                    </button>
                                </li>
                                );
                            })}
                            <li><hr className="dropdown-divider" /></li>
                            <li><button className="dropdown-item" onClick={() => setShowNewBasket(true)}>
                                <i className="bi bi-plus-lg me-1"></i> Create new basket
                            </button></li>
                        </ul>
                    </div>

                    {selectedBasket && (
                        <button
                            className="btn btn-success btn-sm"
                            onClick={handleFetchData}
                            disabled={loading}
                            title={selectedBasket.id === 'csv' ? 'Fetch data using current CSV ASIN list' : 'Fetch latest API data'}
                        >
                            {loading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-arrow-clockwise"></i>}
                            {' '}{selectedBasket.id === 'csv' ? 'Fetch Data (CSV List)' : 'Fetch Data'}
                        </button>
                    )}

                    {selectedBasket && (
                        <button className="btn btn-outline-primary btn-sm" onClick={handleOpenEditFromSelection}>
                            <i className="bi bi-pencil-square me-1"></i>
                            Edit Basket
                        </button>
                    )}

                    {/* CSV Upload */}
                    <div className="d-flex align-items-center gap-2">
                        <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                            <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                            <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                        </label>
                        {csvName && (
                            <span className="badge bg-success d-flex align-items-center gap-1">
                                CSV: {csvName}
                                <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                            </span>
                        )}
                    </div>

                    {/* Period toggle */}
                    <div className="form-check form-switch ms-auto">
                        <input className="form-check-input" type="checkbox" role="switch"
                            checked={period === 'day'}
                            onChange={(e) => setPeriod(e.target.checked ? 'day' : 'week')}
                            id="periodToggle" />
                        <label className="form-check-label" htmlFor="periodToggle">
                            {period === 'day' ? '📅 By day' : '📅 By week'}
                        </label>
                    </div>
                </div>

                {/* ── New basket modal ── */}
                {showNewBasket && (
                    <div className="card mb-4 border-primary">
                        <div className="card-body">
                            <h6>Create New Basket</h6>
                            <form onSubmit={handleCreateBasket}>
                                <div className="row g-2">
                                    <div className="col-md-3">
                                        <input className="form-control form-control-sm" placeholder="Basket name" value={newName} onChange={e => setNewName(e.target.value)} required />
                                    </div>
                                    <div className="col-md-5">
                                        <textarea className="form-control form-control-sm" rows={2} placeholder="ASINs (one per line or comma-separated)" value={newAsins} onChange={e => setNewAsins(e.target.value)} required />
                                    </div>
                                    <div className="col-md-2">
                                        <select className="form-select form-select-sm" value={newCountry} onChange={e => setNewCountry(e.target.value)}>
                                            {['US', 'MX', 'CA', 'UK', 'DE', 'ES', 'FR', 'IT'].map(c =>
                                                <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2 d-flex gap-1">
                                        <button className="btn btn-primary btn-sm" type="submit">Create</button>
                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowNewBasket(false)}>Cancel</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showEditBasket && (
                    <div className="card mb-4 border-info">
                        <div className="card-body">
                            <h6>Edit Basket</h6>
                            <form onSubmit={handleEditBasket}>
                                <div className="row g-2">
                                    <div className="col-md-3">
                                        <input className="form-control form-control-sm" placeholder="Basket name" value={editName} onChange={e => setEditName(e.target.value)} required />
                                    </div>
                                    <div className="col-md-5">
                                        <textarea className="form-control form-control-sm" rows={2} placeholder="ASINs (one per line or comma-separated)" value={editAsins} onChange={e => setEditAsins(e.target.value)} required />
                                    </div>
                                    <div className="col-md-2">
                                        <select className="form-select form-select-sm" value={editCountry} onChange={e => setEditCountry(e.target.value)}>
                                            {['US', 'MX', 'CA', 'UK', 'DE', 'ES', 'FR', 'IT'].map(c =>
                                                <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2 d-flex gap-1">
                                        <button className="btn btn-info btn-sm" type="submit">Save</button>
                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => { setShowEditBasket(false); setEditingBasket(null); }}>Cancel</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ── Main content (only when data exists) ── */}
                {data.length > 0 && (
                    <>
                        {/* ── Overview Section ── */}
                        <h5 className="fw-bold mb-1">Overview — All Brands</h5>
                        <small className="text-muted d-block mb-3">
                            Use the controls below to filter the overview. The metrics summarize the latest period across selected brands.
                        </small>

                        <div className="row mb-4">
                            {/* Left column: Brand selector + Date filter */}
                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label className="form-label text-muted small">Select the brands to filter the overview chart.</label>
                                    <div className="card">
                                        <div className="card-body py-2 px-3">
                                            <div className="d-flex flex-wrap gap-1 mb-2">
                                                {selectedBrands.map(b => (
                                                    <span key={b} className="badge bg-primary d-flex align-items-center gap-1"
                                                        style={{ fontSize: '0.78rem', cursor: 'pointer' }}>
                                                        {b}
                                                        <i className="bi bi-x" onClick={() => removeBrand(b)} style={{ cursor: 'pointer' }}></i>
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="dropdown">
                                                <button className="btn btn-outline-secondary btn-sm dropdown-toggle w-100" data-bs-toggle="dropdown">
                                                    <i className="bi bi-gear me-1"></i> Brands to display (overview)
                                                </button>
                                                <ul className="dropdown-menu" style={{ maxHeight: 200, overflowY: 'auto' }}>
                                                    <li><button className="dropdown-item" onClick={() => setSelectedBrands(allBrands)}>Select All</button></li>
                                                    <li><button className="dropdown-item" onClick={() => setSelectedBrands([])}>Clear All</button></li>
                                                    <li><hr className="dropdown-divider" /></li>
                                                    {allBrands.map(b => (
                                                        <li key={b}>
                                                            <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => toggleBrand(b)}>
                                                                <input type="checkbox" checked={selectedBrands.includes(b)} readOnly />
                                                                {b}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Date filter */}
                                <div>
                                    <label className="form-label text-muted small">
                                        <i className="bi bi-calendar3 me-1"></i> Filter by date (overview)
                                    </label>
                                    <div className="d-flex gap-2">
                                        <input type="date" className="form-control form-control-sm"
                                            value={dateRange.from}
                                            onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))} />
                                        <input type="date" className="form-control form-control-sm"
                                            value={dateRange.to}
                                            onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))} />
                                    </div>
                                </div>
                            </div>

                            {/* Right column: Last period highlights — 3×3 grid */}
                            <div className="col-md-8">
                                <h6 className="fw-bold mb-3">Last period highlights</h6>
                                {highlights ? (
                                    <div className="row g-2">
                                        {/* Column 1: Discounts */}
                                        <div className="col-md-4">
                                            <HighlightCard
                                                icon="🏷️" color="#22C55E"
                                                label={`Highest discount — ${highlights.label}`}
                                                sublabel={highlights.highestDisc?.brand}
                                                value={highlights.highestDisc ? `${Number(highlights.highestDisc.discount_pct).toFixed(1)}%` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="🏷️" color="#22C55E"
                                                label={`Lowest discount — ${highlights.label}`}
                                                sublabel={highlights.lowestDisc?.brand}
                                                value={highlights.lowestDisc ? `${Number(highlights.lowestDisc.discount_pct).toFixed(1)}%` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="🏷️" color="#22C55E"
                                                label={`Average discount — ${highlights.label}`}
                                                value={highlights.avgDiscount != null ? `${Number(highlights.avgDiscount).toFixed(1)}%` : 'N/A'}
                                            />
                                        </div>
                                        {/* Column 2: Prices */}
                                        <div className="col-md-4">
                                            <HighlightCard
                                                icon="💲" color="#3B82F6"
                                                label={`Highest price — ${highlights.label}`}
                                                sublabel={highlights.highestPrice?.brand}
                                                value={highlights.highestPrice && highlights.highestPrice.product_price != null ? `$${Number(highlights.highestPrice.product_price).toFixed(2)}` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="💲" color="#3B82F6"
                                                label={`Lowest price — ${highlights.label}`}
                                                sublabel={highlights.lowestPrice?.brand}
                                                value={highlights.lowestPrice && highlights.lowestPrice.product_price != null ? `$${Number(highlights.lowestPrice.product_price).toFixed(2)}` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="💲" color="#3B82F6"
                                                label={`Average price — ${highlights.label}`}
                                                value={highlights.avgPrice != null ? `$${Number(highlights.avgPrice).toFixed(2)}` : 'N/A'}
                                            />
                                        </div>
                                        {/* Column 3: Price Changes */}
                                        <div className="col-md-4">
                                            <HighlightCard
                                                icon="🔺" color="#EF4444"
                                                label={`Largest price change — ${highlights.label}`}
                                                sublabel={highlights.largestChange?.brand}
                                                value={highlights.largestChange ? `${Number(highlights.largestChange.price_change) > 0 ? '+' : ''}${Number(highlights.largestChange.price_change).toFixed(1)}%` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="🔻" color="#EF4444"
                                                label={`Lowest price change — ${highlights.label}`}
                                                sublabel={highlights.lowestChange?.brand}
                                                value={highlights.lowestChange ? `${Number(highlights.lowestChange.price_change) > 0 ? '+' : ''}${Number(highlights.lowestChange.price_change).toFixed(1)}%` : 'N/A'}
                                            />
                                            <HighlightCard
                                                icon="↕" color="#F59E0B"
                                                label={`Average price change — ${highlights.label}`}
                                                value={highlights.avgChange != null ? `${Number(highlights.avgChange) > 0 ? '+' : ''}${Number(highlights.avgChange).toFixed(1)}%` : 'N/A'}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-muted">No data for the selected period.</p>
                                )}
                            </div>
                        </div>

                        {/* ── Overview Chart ── */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <Plot
                                    data={buildOverviewTraces()}
                                    layout={{
                                        title: `Overview — ${period === 'day' ? 'Daily' : 'Weekly'} Price by Brand`,
                                        height: 420,
                                        hovermode: 'x unified',
                                        legend: { title: { text: 'Brand' } },
                                        margin: { l: 60, r: 20, t: 50, b: 55 },
                                        xaxis: { title: { text: period === 'day' ? 'Date' : 'Week Number' }, ...(period === 'day' ? { type: 'date', tickformat: '%b %d' } : { type: 'category', tickprefix: 'Wk ' }) },
                                        yaxis: { title: { text: 'Product Price (USD)' }, tickprefix: '$' },
                                    }}
                                    config={{ responsive: true }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>

                        {/* ── Product Gallery ── */}
                        {(() => {
                            const latestRows = latestDate
                                ? data.filter(r => r.date === latestDate)
                                : [];

                            // Dedup by ASIN (prefer record with price if multiple exist)
                            const uniqueRows = Array.from(
                                latestRows.reduce((map, row) => {
                                    if (!map.has(row.asin) || (row.product_price != null && map.get(row.asin).product_price == null)) {
                                        map.set(row.asin, row);
                                    }
                                    return map;
                                }, new Map()).values()
                            ).sort((a, b) => a.asin.localeCompare(b.asin));

                            return uniqueRows.length > 0 ? (
                                <div className="card">
                                    <div className="card-body">
                                        <h5 className="card-title">Current Products</h5>
                                        <div className="row g-3">
                                            {uniqueRows.map(row => (
                                                <div key={row.asin} className="col-md-3 col-sm-6">
                                                    <ProductCard row={row} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : null;
                        })()}
                    </>
                )}

                {/* Empty states */}
                {data.length === 0 && !loading && selectedBasket && (
                    <div className="text-center text-muted py-5">
                        <h5>No data yet</h5>
                        <p>Click <strong>Fetch Data</strong> to get competitor data from Amazon</p>
                    </div>
                )}

                {!selectedBasket && (
                    <div className="text-center text-muted py-5">
                        <i className="bi bi-basket2 display-4 d-block mb-3"></i>
                        <h5>Select or create a basket to start monitoring</h5>
                    </div>
                )}
            </div>
        </Layout>
    );
}

/* ── Highlight metric card ── */
function HighlightCard({ icon, color, label, sublabel, value }) {
    return (
        <div className="mb-2 p-2 rounded" style={{ background: '#f8f9fa', borderLeft: `3px solid ${color}` }}>
            <div style={{ fontSize: '0.7rem', color: '#666' }}>
                {icon} {label}{sublabel ? ` — ${sublabel}` : ''}
            </div>
            <div className="fw-bold" style={{ fontSize: '1.3rem' }}>{value}</div>
        </div>
    );
}

export default TrackingOverview;
